/**
 * Pure formatters used by the Task views. No React/DOM dependencies so
 * they're trivially unit-testable.
 */
import type { Task, TaskPriority, TaskStatus } from "./types";

export function formatDate(ts: number | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}/${mm}/${dd}`;
}

export function formatDateTime(ts: number | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${formatDate(ts)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function priorityRank(p: TaskPriority): number {
  return { P0: 0, P1: 1, P2: 2, P3: 3 }[p];
}

export function statusRank(s: TaskStatus): number {
  return { todo: 0, doing: 1, review: 2, done: 3 }[s];
}

/** Tailwind classes for priority chip — light theme only. */
export function priorityChipClass(p: TaskPriority): string {
  switch (p) {
    case "P0":
      return "bg-red-50 text-red-700 ring-1 ring-red-200";
    case "P1":
      return "bg-orange-50 text-orange-700 ring-1 ring-orange-200";
    case "P2":
      return "bg-blue-50 text-blue-700 ring-1 ring-blue-200";
    case "P3":
      return "bg-slate-100 text-slate-600 ring-1 ring-slate-200";
  }
}

/** Tailwind classes for status chip — light theme only. */
export function statusChipClass(s: TaskStatus): string {
  switch (s) {
    case "todo":
      return "bg-slate-100 text-slate-700";
    case "doing":
      return "bg-amber-50 text-amber-800";
    case "review":
      return "bg-purple-50 text-purple-700";
    case "done":
      return "bg-emerald-50 text-emerald-700";
  }
}

export function isOverdue(task: Task, now: number = Date.now()): boolean {
  if (task.status === "done") return false;
  return task.due_at !== null && task.due_at < now;
}

export function compareTasks(a: Task, b: Task): number {
  const sa = statusRank(a.status);
  const sb = statusRank(b.status);
  if (sa !== sb) return sa - sb;
  if (a.position !== b.position) return a.position - b.position;
  const pa = priorityRank(a.priority);
  const pb = priorityRank(b.priority);
  if (pa !== pb) return pa - pb;
  return a.created_at - b.created_at;
}
